import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CreateServiceDialog } from '@/components/dialogs/commercial/price-table/create-service-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { centsToPtbr } from '@/lib/formatters';

interface ServiceTier {
  id: string;
  service: string;
  monthlyPrice: string;
  annualPrice: string;
  fixedPrice: string;
}

interface ServicePriceFormProps {
  onPriceChange?: (prices: any) => void;
  initialData?: Array<{
    serviceId: string;
    monthlyPriceInCents?: number;
    yearlyPriceInCents?: number;
    fixedPriceInCents?: number;
  }>;
  disabled?: boolean;
}

export function ServicePriceForm({
  onPriceChange,
  initialData,
  disabled = false,
}: ServicePriceFormProps) {
  const [showForm, setShowForm] = useState(!!initialData && initialData.length > 0);
  const [serviceTiers, setServiceTiers] = useState<ServiceTier[]>(() => {
    if (initialData && initialData.length > 0) {
      return initialData.map((item, index) => ({
        id: (index + 1).toString(),
        service: item.serviceId || '',
        monthlyPrice: centsToPtbr(item.monthlyPriceInCents),
        annualPrice: centsToPtbr(item.yearlyPriceInCents),
        fixedPrice: centsToPtbr(item.fixedPriceInCents),
      }));
    }
    return [
      {
        id: '1',
        service: '',
        monthlyPrice: '',
        annualPrice: '',
        fixedPrice: '',
      },
    ];
  });

  // Fetch services from API
  const { data: servicesData } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data } = await api.get<{ docs: Array<{ id: string; name: string }> }>(
        '/commercial/services',
      );
      return data.docs || [];
    },
  });

  const serviceOptions =
    servicesData?.map((service) => ({
      id: service.id,
      text: service.name,
    })) || [];

  // Sync state when initialData arrives after mount (e.g. API fetch completes)
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setShowForm(true);
      setServiceTiers(
        initialData.map((item, index) => ({
          id: (index + 1).toString(),
          service: item.serviceId || '',
          monthlyPrice: centsToPtbr(item.monthlyPriceInCents),
          annualPrice: centsToPtbr(item.yearlyPriceInCents),
          fixedPrice: centsToPtbr(item.fixedPriceInCents),
        })),
      );
    }
  }, [initialData]);

  // Track if this is the initial mount to avoid notifying parent on mount
  const isInitialMount = useRef(true);

  // Notify parent when serviceTiers change (after state update)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onPriceChange) {
      onPriceChange(serviceTiers);
    }
  }, [serviceTiers, onPriceChange]);

  const addServiceTier = () => {
    if (disabled) return;
    if (!showForm) {
      setShowForm(true);
      return;
    }

    // Add new tier
    const newTier: ServiceTier = {
      id: Date.now().toString(),
      service: '',
      monthlyPrice: '',
      annualPrice: '',
      fixedPrice: '',
    };

    setServiceTiers((prev) => [...prev, newTier]);
  };

  const removeServiceTier = (id: string) => {
    if (disabled) return;

    const remaining = serviceTiers.filter((tier) => tier.id !== id);
    if (remaining.length === 0) {
      setShowForm(false);
      setServiceTiers([
        {
          id: '1',
          service: '',
          monthlyPrice: '',
          annualPrice: '',
          fixedPrice: '',
        },
      ]);
      onPriceChange?.([]);
      return;
    }

    setServiceTiers(remaining);
  };

  const updateServiceTier = (id: string, field: keyof ServiceTier, value: string) => {
    if (disabled) return;
    setServiceTiers((prev) =>
      prev.map((tier) => (tier.id === id ? { ...tier, [field]: value } : tier)),
    );
  };

  return (
    <div className="space-y-6 border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-lg text-gray-900">Serviços</h3>

      {!showForm ? (
        /* Initial Button */
        <Button
          variant="outline"
          onClick={addServiceTier}
          className="w-full"
          type="button"
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar configuração
        </Button>
      ) : (
        /* Service Pricing Form */
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <CardTitle className="text-base">Configuração de Serviços</CardTitle>
              {!disabled && <CreateServiceDialog />}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {/* Service Tiers */}
              {serviceTiers.map((tier) => (
                <div key={tier.id} className="flex flex-col gap-4 p-4 border rounded-lg">
                  {/* Service Configuration */}
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Serviço</Label>
                      <Combobox
                        data={serviceOptions}
                        keyExtractor={(item) => item.id}
                        displayValueGetter={(item) => item.text}
                        type="single"
                        value={
                          tier.service
                            ? serviceOptions.filter((opt) => opt.id === tier.service)
                            : []
                        }
                        onOptionChange={(value) => {
                          updateServiceTier(tier.id, 'service', value[0]?.id || '');
                        }}
                        placeholder="Selecione um serviço"
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  {/* Price Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Preço mensal</Label>
                      <CurrencyInput
                        value={tier.monthlyPrice || '0,00'}
                        onChange={(v) => updateServiceTier(tier.id, 'monthlyPrice', v)}
                        placeholder="0,00"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Preço anual</Label>
                      <CurrencyInput
                        value={tier.annualPrice || '0,00'}
                        onChange={(v) => updateServiceTier(tier.id, 'annualPrice', v)}
                        placeholder="0,00"
                        disabled={disabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Preço fixo</Label>
                      <CurrencyInput
                        value={tier.fixedPrice || '0,00'}
                        onChange={(v) => updateServiceTier(tier.id, 'fixedPrice', v)}
                        placeholder="0,00"
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  {/* Remove button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeServiceTier(tier.id)}
                      type="button"
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Add New Configuration Button */}
              <Button
                variant="outline"
                onClick={addServiceTier}
                className="w-full"
                type="button"
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar configuração
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
